import { useCallback, useEffect, useRef, useState } from "react";

import { Stack } from "@mui/material";
import { UIMessage } from "ai";

import { AssistantConfigProvider } from "../contexts/AssistantConfigContext";
import { ConversationStorage } from "../helpers/conversationStorage";
import { getLatestFeedbackRequestId, hasShownFeedbackRequest } from "../helpers/feedbackRequest";
import { AssistantChatAdapter, AssistantChatControls, AssistantChatState } from "../helpers/types";
import { AssistantFeedbackAnswer, useAssistantFeedback } from "../helpers/useAssistantFeedback";
import { useConversationRestore } from "../helpers/useConversationRestore";

import { DonorAssistantMessageSent } from "./Chat";

import { ExitFeedback } from "@/components/AIElements/ExitFeedback/ExitFeedback";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { DashboardZeffyLoader } from "@/dashboard-design-system/ZeffyLoader/DashboardZeffyLoader";

const CLOSING_MESSAGE_READING_MS = 2000;

const exitImmediately = (exit: () => void) => exit();

interface ConnectedChatProps<TChat extends AssistantChatState, TControls extends AssistantChatControls> {
  adapter: AssistantChatAdapter<TChat, TControls>;
  onControlsChange?: (controls: TControls) => void;
  onMessageSent?: (params: DonorAssistantMessageSent) => void;
  onToolRendered?: (params: { toolName: string }) => void;
}

export const ConnectedChat = <TChat extends AssistantChatState, TControls extends AssistantChatControls>({
  adapter,
  ...props
}: ConnectedChatProps<TChat, TControls>) => {
  const scope = adapter.useScope();
  return (
    <AssistantConfigProvider value={adapter.config}>
      {scope ? <ScopedChat key={scope.key} adapter={adapter} storage={scope.storage} {...props} /> : LOADER}
    </AssistantConfigProvider>
  );
};

const LOADER = (
  <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
    <DashboardZeffyLoader size="large" />
  </Stack>
);

type ScopedChatProps<TChat extends AssistantChatState, TControls extends AssistantChatControls> = ConnectedChatProps<
  TChat,
  TControls
> & { storage: ConversationStorage };

const ScopedChat = <TChat extends AssistantChatState, TControls extends AssistantChatControls>({
  storage,
  ...props
}: ScopedChatProps<TChat, TControls>) => {
  const [session, setSession] = useState(() => ({ id: storage.read(), generation: 0 }));
  const startNew = useCallback(() => {
    storage.save(null);
    setSession((previous) => ({ id: null, generation: previous.generation + 1 }));
  }, [storage]);
  const reload = useCallback(
    () => setSession((previous) => ({ id: storage.read(), generation: previous.generation + 1 })),
    [storage]
  );
  return (
    <SessionChat
      key={`${session.id ?? "new"}:${session.generation}`}
      aiSessionId={session.id}
      storage={storage}
      startNew={startNew}
      reload={reload}
      {...props}
    />
  );
};

type SessionChatProps<TChat extends AssistantChatState, TControls extends AssistantChatControls> = ScopedChatProps<
  TChat,
  TControls
> & { aiSessionId: string | null; startNew: () => void; reload: () => void };

const SessionChat = <TChat extends AssistantChatState, TControls extends AssistantChatControls>({
  adapter,
  storage,
  aiSessionId,
  startNew,
  reload,
  onControlsChange,
  onMessageSent,
  onToolRendered,
}: SessionChatProps<TChat, TControls>) => {
  const { t } = useTranslate();
  const { conversation, view } = useConversationRestore(adapter.useConversation, aiSessionId);
  const saveSession = useCallback((id: string) => storage.save(id), [storage]);
  useReportedControls(
    onControlsChange,
    view === "chat"
      ? null
      : adapter.toControls(
          { startNew, isBusy: view === "loading", messageCount: 0, requestExit: exitImmediately },
          null
        )
  );
  if (view === "loading") {
    return LOADER;
  }
  if (view === "limit") {
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <DashboardTypography role="alert">{t("dashboard", "assistant.recovery.limit")}</DashboardTypography>
        <DashboardButton onClick={startNew}>{t("dashboard", "assistant.recovery.new")}</DashboardButton>
      </Stack>
    );
  }
  if (view === "failed") {
    return (
      <Stack spacing={2} sx={{ p: 3 }}>
        <DashboardTypography role="alert">{t("dashboard", "assistant.recovery.error")}</DashboardTypography>
        <DashboardButton
          onClick={() => {
            void conversation.refetch();
          }}
        >
          {t("dashboard", "assistant.recovery.retry")}
        </DashboardButton>
        <DashboardButton variant="outlined" onClick={startNew}>
          {t("dashboard", "assistant.recovery.new")}
        </DashboardButton>
      </Stack>
    );
  }
  return (
    <RestoredChat
      adapter={adapter}
      aiSessionId={aiSessionId ?? undefined}
      messages={aiSessionId ? ((conversation.data?.messages ?? []) as UIMessage[]) : []}
      canResume={Boolean(aiSessionId && conversation.data?.canResume)}
      initialFeedback={aiSessionId ? (conversation.data?.feedback ?? null) : null}
      startNew={startNew}
      onReload={reload}
      onControlsChange={onControlsChange}
      onMessageSent={onMessageSent}
      onToolRendered={onToolRendered}
      onSessionCreated={saveSession}
    />
  );
};

type RestoredChatProps<TChat extends AssistantChatState, TControls extends AssistantChatControls> = ConnectedChatProps<
  TChat,
  TControls
> & {
  aiSessionId?: string;
  messages: UIMessage[];
  canResume: boolean;
  initialFeedback: AssistantFeedbackAnswer | null;
  startNew: () => void;
  onReload: () => void;
  onSessionCreated: (id: string) => void;
};

const RestoredChat = <TChat extends AssistantChatState, TControls extends AssistantChatControls>({
  adapter,
  startNew,
  onReload,
  onControlsChange,
  onMessageSent,
  initialFeedback,
  ...initial
}: RestoredChatProps<TChat, TControls>) => {
  const { t } = useTranslate();
  const chat = adapter.useAssistant(initial);
  const feedbackMutations = adapter.useFeedbackMutations();
  const { isBusy, messages } = chat;
  const { showOnExit, ...feedback } = useAssistantFeedback({
    mutations: feedbackMutations,
    getAiSessionId: chat.getAiSessionId,
    initialFeedback,
    isShownInThread: hasShownFeedbackRequest(messages),
    hasAssistantReply: messages.some((message) => message.role === "assistant"),
  });
  const [pendingExit, setPendingExit] = useState<{ exit: () => void } | null>(null);
  const feedbackRequestId = getLatestFeedbackRequestId(messages);
  const [restoredRequestId] = useState(initialFeedback?.rating ? feedbackRequestId : null);
  const [dismissedRequestId, setDismissedRequestId] = useState<string | null>(null);
  const pendingRequestId =
    feedbackRequestId !== restoredRequestId && feedbackRequestId !== dismissedRequestId ? feedbackRequestId : null;
  const [readRequestId, setReadRequestId] = useState(pendingRequestId);
  useEffect(() => {
    if (isBusy || pendingRequestId === null || pendingRequestId === readRequestId) {
      return;
    }
    const timer = setTimeout(() => setReadRequestId(pendingRequestId), CLOSING_MESSAGE_READING_MS);
    return () => clearTimeout(timer);
  }, [isBusy, pendingRequestId, readRequestId]);
  const isRequestedSurveyOpen = pendingRequestId !== null && pendingRequestId === readRequestId;
  const latest = useRef({ isBusy, showOnExit, pendingExit });
  useEffect(() => {
    latest.current = { isBusy, showOnExit, pendingExit };
  });
  const finishExit = useCallback((exit: () => void) => {
    setPendingExit(null);
    exit();
  }, []);
  const isPreparingExit = useRef(false);
  const requestExit = useCallback(
    (exit: () => void) => {
      const current = latest.current;
      if (current.pendingExit || current.isBusy) {
        finishExit(exit);
        return;
      }
      if (isPreparingExit.current) {
        return;
      }
      isPreparingExit.current = true;
      void current
        .showOnExit()
        .then(
          (isShown) => (isShown ? setPendingExit({ exit }) : exit()),
          () => exit()
        )
        .finally(() => {
          isPreparingExit.current = false;
        });
    },
    [finishExit]
  );
  useReportedControls(
    onControlsChange,
    adapter.toControls({ startNew, isBusy, messageCount: messages.length, requestExit }, chat)
  );
  const { ChatView } = adapter;
  const question = t("dashboard", "assistant.feedback.question");
  if (pendingExit) {
    return <ExitFeedback question={question} feedback={feedback} onDone={() => finishExit(pendingExit.exit)} />;
  }
  return (
    <>
      <Stack inert={isRequestedSurveyOpen} sx={{ flex: 1, minHeight: 0 }}>
        <ChatView chat={chat} onStartNew={startNew} onReload={onReload} onMessageSent={onMessageSent} />
      </Stack>
      {isRequestedSurveyOpen && (
        <ExitFeedback question={question} feedback={feedback} onDone={() => setDismissedRequestId(feedbackRequestId)} />
      )}
    </>
  );
};

const useReportedControls = <TControls extends AssistantChatControls>(
  onControlsChange: ((controls: TControls) => void) | undefined,
  controls: TControls | null
) => {
  const reported = useRef<{ controls: TControls; onControlsChange?: (controls: TControls) => void } | null>(null);
  useEffect(() => {
    if (!controls) {
      reported.current = null;
      return;
    }
    const previous = reported.current;
    if (previous && previous.onControlsChange === onControlsChange && haveSameValues(previous.controls, controls)) {
      return;
    }
    reported.current = { controls, onControlsChange };
    onControlsChange?.(controls);
  });
};

const haveSameValues = (previous: object, next: object): boolean => {
  const previousValues = new Map<string, unknown>(Object.entries(previous));
  const nextEntries: [string, unknown][] = Object.entries(next);
  return (
    previousValues.size === nextEntries.length &&
    nextEntries.every(([key, value]) => previousValues.has(key) && Object.is(previousValues.get(key), value))
  );
};
